import React, { useRef, useEffect, useState } from 'react';
import { Dropdown, MenuItem } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

interface IFilterDropdownProps {
    value: string;
    options: Array<{ value: string; label: string; color?: string }>;
    onChange: (value: string) => void;
    placeholder: string;
    defaultValue?: string;
    disabled?: boolean;
}

const FilterDropdown: React.FC<IFilterDropdownProps> = ({
    value,
    options,
    onChange,
    placeholder,
    defaultValue = '',
    disabled = false
}) => {
    const selectedOption = options.find(option => option.value === value);
    const displayText = selectedOption ? selectedOption.label : placeholder;

    // Split text on ": " to separate label from value
    const parts = displayText.split(': ');
    const labelPart = parts.length > 1 ? parts[0] : null;
    const valuePart =
        parts.length > 1 ? parts.slice(1).join(': ') : displayText;
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [positionClass, setPositionClass] = useState('dropdown-left');

    const handleSelect = (selectedValue: any) => {
        if (selectedValue) {
            onChange(selectedValue);
        }
    };

    const calculatePosition = () => {
        if (!wrapperRef.current) return;

        const wrapper = wrapperRef.current;
        const wrapperRect = wrapper.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const dropdownWidth = 250; // Approximate dropdown width

        // Check if dropdown would overflow with default left alignment
        const wouldOverflowRight =
            wrapperRect.left + dropdownWidth > screenWidth - 20;

        // Check if dropdown would overflow with right alignment
        const wouldOverflowLeft = wrapperRect.right - dropdownWidth < 20;

        if (wouldOverflowRight && wouldOverflowLeft) {
            // Center if both would overflow
            setPositionClass('dropdown-center');
        } else if (wouldOverflowRight) {
            // Right align if left would overflow
            setPositionClass('dropdown-right');
        } else {
            // Default left alignment (no class needed)
            setPositionClass('');
        }
    };

    const handleToggle = (isOpen: boolean) => {
        if (isOpen) {
            // Use setTimeout to ensure DOM is updated
            setTimeout(() => {
                calculatePosition();
            }, 0);
        }
    };

    const handleReset = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(defaultValue);
    };

    // Check if the current value is a "default" state (shouldn't show reset icon)
    const isDefaultValue =
        value === defaultValue ||
        value === 'All' ||
        value === 'No Options' ||
        value === '' ||
        !value;

    useEffect(() => {
        const handleResize = () => {
            calculatePosition();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div ref={wrapperRef} className="filter-dropdown-wrapper">
            <Dropdown
                onSelect={handleSelect}
                onToggle={handleToggle}
                disabled={disabled}
                className="filter-dropdown"
                id="filter-dropdown"
            >
                <Dropdown.Toggle
                    noCaret
                    disabled={disabled}
                    className="filter-dropdown-toggle"
                    style={
                        selectedOption?.color
                            ? ({
                                  '--filter-color': selectedOption.color
                              } as React.CSSProperties)
                            : undefined
                    }
                >
                    {selectedOption?.color && (
                        <span className="filter-color-indicator" />
                    )}
                    <span className="filter-dropdown-text">
                        {labelPart ? (
                            <>
                                <span className="filter-label">
                                    {labelPart}:
                                </span>{' '}
                                <span className="filter-value">
                                    {valuePart}
                                </span>
                            </>
                        ) : (
                            displayText
                        )}
                    </span>
                    {!isDefaultValue ? (
                        <span
                            className="filter-dropdown-reset-icon-wrapper"
                            onClick={handleReset}
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="filter-dropdown-icon filter-dropdown-reset-icon"
                            />
                        </span>
                    ) : (
                        <FontAwesomeIcon
                            icon="chevron-down"
                            className="filter-dropdown-icon"
                        />
                    )}
                </Dropdown.Toggle>

                <Dropdown.Menu
                    className={`filter-dropdown-menu ${positionClass}`}
                >
                    {options.map(option => (
                        <MenuItem
                            key={option.value}
                            eventKey={option.value}
                            active={option.value === value}
                            className="filter-dropdown-item"
                            style={
                                option.color
                                    ? ({
                                          '--filter-color': option.color
                                      } as React.CSSProperties)
                                    : undefined
                            }
                        >
                            {option.color && (
                                <span className="filter-color-indicator" />
                            )}
                            <span className="filter-option-text">
                                {option.label}
                            </span>
                            {option.value === value && (
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className="filter-check-icon"
                                />
                            )}
                        </MenuItem>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

export default FilterDropdown;
